/**
 * Centralized Magic landing copy, gallery, and CTAs.
 * Update here before changing the page layout.
 */

import { PAGE_ROUTES } from "@/app/_utils/constants";

export const MAGIC_PAGE_FONT =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

export const MAGIC_PAGE_BG = "#0a0a0a";
export const MAGIC_MUTED = "rgba(255,255,255,0.72)";
export const MAGIC_MUTED_SOFT = "rgba(255,255,255,0.55)";
export const MAGIC_BORDER = "rgba(255,255,255,0.14)";

/** Verified in product surfaces (ticket pages, ShowInfoPanel). */
export const MAGIC_INSTAGRAM_HANDLE = "magic.uo";
export const MAGIC_INSTAGRAM_URL = "https://www.instagram.com/magic.uo/";

export const MAGIC_ROUTES = {
  privateEvents: PAGE_ROUTES.PRIVATE_EVENTS,
  publicShows: PAGE_ROUTES.TICKETS,
} as const;

/** ComedyUO magic show imagery (GCS). */
export const MAGIC_IMAGES = {
  hero: {
    src: "https://storage.googleapis.com/cuo-website/Title%20Card%20Magic%20Show.jpg",
    alt: "ComedyUO magic show title card",
  },
  privateEvents: {
    src: "https://storage.googleapis.com/cuo-website/Dim%20Sum%20Magic%20Show.jpg",
    alt: "ComedyUO magic at a dim sum private event",
  },
  publicShows: {
    src: "https://storage.googleapis.com/cuo-website/Astor%20Place%20Magic%20Show.jpg",
    alt: "ComedyUO magic show at Astor Place",
  },
  quoteBg: {
    src: "https://storage.googleapis.com/cuo-website/Title%20Card%20Magic%20Show.jpg",
    alt: "",
  },
  thirteenthStorey: {
    src: "https://storage.googleapis.com/cuo-website/Rooms%20We've%20Fooled%20Magic%20Show.jpg",
    alt: "ComedyUO magic at 13th Storey",
  },
  burton: {
    src: "https://storage.googleapis.com/cuo-website/Burton%20Magic%20Show.jpg",
    alt: "ComedyUO magic at Burton",
  },
  bodega: {
    // Prefer public googleapis host (same object as storage.cloud.google.com)
    src: "https://storage.googleapis.com/cuo-website/Bodega%20Magic%20Show.jpg",
    alt: "ComedyUO bodega magic show",
  },
  gothamBurger: {
    src: "https://storage.googleapis.com/cuo-website/Gotham%20Burger%20Magic%20Show.jpg",
    alt: "ComedyUO magic at Gotham Burger",
  },
  fetteSau: {
    src: "https://storage.googleapis.com/cuo-website/Fette%20Sau%20(RIP).jpg",
    alt: "ComedyUO magic at Fette Sau",
  },
} as const;

export const MAGIC_HERO = {
  eyebrow: "Magic by ComedyUO",
  headlineLead: "Magic in",
  headlineEmphasis: "Unexpected Places.",
  body: "Close-up magic, mentalism and stage illusions built for private events, ticketed shows, and branded activations.",
  primaryCta: {
    label: "Plan a private magic experience",
    href: MAGIC_ROUTES.privateEvents,
  },
  secondaryCta: {
    label: "See public shows",
    href: MAGIC_ROUTES.publicShows,
  },
} as const;

export const MAGIC_WAYS = {
  eyebrow: "Two ways to work with us",
  headline: "Private events and public shows.",
  body: "ready for boardrooms, basements, and more",
  cards: [
    {
      id: "private-events",
      kind: "image" as const,
      label: "Private Events",
      headline: "We do magic for private events",
      body: "Close-up magic, mentalism and stage performances for corporate dinners, client entertainment, birthdays, weddings and member clubs. Book one magician to work the room or build a full show for a seated crowd.",
      cta: {
        label: "Book us for an event",
        href: MAGIC_ROUTES.privateEvents,
      },
      image: MAGIC_IMAGES.privateEvents,
    },
    {
      id: "public-shows",
      kind: "image" as const,
      label: "Public Shows",
      headline: "Magic shows you won’t find anywhere else",
      body: "Ticketed magic nights in unexpected New York City rooms, from historic restaurants and hidden basements to studios and rooftops. Small capacities, no bad seats and always something you can’t explain on the train home.",
      cta: {
        label: "See upcoming shows",
        href: MAGIC_ROUTES.publicShows,
      },
      image: MAGIC_IMAGES.publicShows,
    },
  ],
} as const;

export type MagicGalleryItem =
  | {
      id: string;
      kind: "image";
      src: string;
      alt: string;
      caption?: string;
      mono?: boolean;
      span: "feature" | "wide" | "square";
    }
  | {
      id: string;
      kind: "more";
      label: string;
      sublabel?: string;
      span: "wide" | "square";
    };

export const MAGIC_GALLERY: {
  headline: string;
  items: MagicGalleryItem[];
} = {
  headline: "Rooms we’ve fooled.",
  items: [
    {
      id: "thirteenth-storey",
      kind: "image",
      src: MAGIC_IMAGES.thirteenthStorey.src,
      alt: MAGIC_IMAGES.thirteenthStorey.alt,
      caption: "13th Storey",
      span: "feature",
    },
    {
      id: "burton",
      kind: "image",
      src: MAGIC_IMAGES.burton.src,
      alt: MAGIC_IMAGES.burton.alt,
      caption: "Burton",
      span: "wide",
    },
    {
      id: "bodega",
      kind: "image",
      src: MAGIC_IMAGES.bodega.src,
      alt: MAGIC_IMAGES.bodega.alt,
      caption: "Bodega Show",
      span: "wide",
    },
    {
      id: "gotham-burger",
      kind: "image",
      src: MAGIC_IMAGES.gothamBurger.src,
      alt: MAGIC_IMAGES.gothamBurger.alt,
      caption: "Gotham Burger",
      span: "square",
    },
    {
      id: "fette-sau",
      kind: "image",
      src: MAGIC_IMAGES.fetteSau.src,
      alt: MAGIC_IMAGES.fetteSau.alt,
      caption: "Fette Sau (RIP)",
      span: "square",
    },
  ],
};

/**
 * People exclusive covering ComedyUO’s magic expansion.
 * https://people.com/underground-overground-comedy-duo-announces-unexpected-new-venture-exclusive-12013357
 */
export const MAGIC_PRESS = {
  publication: "People Magazine",
  quote:
    "ComedyUO went viral for Their Unconventional Shows. Now They Have a New Trick Up Their Sleeve (Exclusive)",
  href: "https://people.com/underground-overground-comedy-duo-announces-unexpected-new-venture-exclusive-12013357",
  needsVerification: false,
} as const;

export const MAGIC_CLOSING = {
  headline: "Looking for something magical? Check us out.",
  body: "New clips from private events, public shows and branded work go up throughout the week on Instagram.",
  primaryCta: {
    label: `Follow @${MAGIC_INSTAGRAM_HANDLE}`,
    href: MAGIC_INSTAGRAM_URL,
    external: true,
  },
  secondaryCta: {
    label: "Inquire about a booking",
    href: MAGIC_ROUTES.privateEvents,
  },
} as const;

export const MAGIC_SEO = {
  title: "NYC Magic Shows & Private Event Magicians | ComedyUO",
  description:
    "ComedyUO produces close-up magic, mentalism and stage shows for private events, public audiences and branded experiences in unexpected New York City venues.",
  keywords: [
    "NYC magic shows",
    "magicians for private events",
    "corporate event entertainment NYC",
    "close-up magic New York",
    "mentalism NYC",
    "branded magic experiences",
    "ComedyUO magic",
    "Magic UO",
    "private event magician NYC",
  ],
  ogImagePath: "/images/og-default-katz-deli.jpg",
} as const;
